"use client";

/**
 * AuthContext — single source of truth for the authenticated user.
 *
 * Wrap the root layout body with <AuthProvider> so every client component
 * can call useAuth() without prop drilling.
 */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { onAuthStateChanged, type User } from "firebase/auth";
import { useRouter } from "next/navigation";
import { getFirebaseAuth, signInWithGoogle, signInWithEmail, signOut } from "@/platform/firebase/auth";
import {
  getUserProfile,
  createUserProfile,
  updateUserProfile,
  buildInitialProfile,
} from "@/platform/firebase/firestore/users";
import type { UserProfile } from "@/shared/types";

// ─── Context shape ────────────────────────────────────────────────────────────

interface AuthContextValue {
  /** Raw Firebase user — null when signed out */
  user: User | null;
  /** Enriched Firestore profile — null until loaded or when signed out */
  profile: UserProfile | null;
  /** True while the initial auth state is being resolved */
  loading: boolean;
  /** Re-fetch the Firestore profile — call after a subscription update */
  refreshProfile: () => Promise<void>;
  signInWithGoogle: () => Promise<{ user: User; isNewUser: boolean }>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

// ─── Context ─────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null);

// ─── Provider ────────────────────────────────────────────────────────────────

// Tracks whether the current auth event is a fresh sign-in (not a restored
// session from persistence). Set to true by the sign-in helpers below before
// Firebase fires onAuthStateChanged, then cleared after the first event.
let _isFreshSignIn = false;

/**
 * Call this immediately before any Firebase sign-in call that is NOT going
 * through AuthContext (e.g. direct signInWithEmailAndPassword in login page).
 * This tells AuthContext that the next onAuthStateChanged event is a real
 * login and should enforce the domain redirect.
 */
export function markFreshSignIn(): void {
  _isFreshSignIn = true;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();

  useEffect(() => {
    const auth = getFirebaseAuth();

    // `authStateReady()` resolves once Firebase has finished reading the
    // persisted auth state from indexedDB/localStorage. This prevents the
    // common race where `onAuthStateChanged` fires with `null` on the first
    // call (before persistence is loaded) — which was causing the Stripe
    // success page to show "must be logged in" immediately after returning
    // from the Stripe hosted checkout page.
    auth.authStateReady().then(() => {
      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        setUser(firebaseUser);

        // Capture and clear the fresh-sign-in flag before any await so it
        // isn't accidentally re-read by a concurrent auth event.
        const isFreshSignIn = _isFreshSignIn;
        _isFreshSignIn = false;

        if (firebaseUser) {
          // Always attempt to load an existing profile — old users registered
          // before email verification was required have emailVerified: false but
          // have a valid Firestore document that must still be loaded.
          let userProfile = await getUserProfile(firebaseUser.uid);

          if (!userProfile) {
            // Create a profile immediately on registration so the user appears
            // in the admin portal right away — email verification is not required.
            // Note this is the *fallback* writer: the signup page builds the
            // same profile with the name already in hand, and this listener can
            // see the new user before `updateProfile` has landed.
            await createUserProfile(firebaseUser.uid, buildInitialProfile(firebaseUser));
            userProfile = await getUserProfile(firebaseUser.uid);
          }

          // The full name can lose a race on signup, which is why it only
          // sometimes reached the database.
          //
          // `registerWithEmail` runs `createUserWithEmailAndPassword` and then
          // `await updateProfile(...)`. This listener fires in between the two,
          // so it can read `firebaseUser.displayName` while it is still null,
          // hand that to the transaction above, and create the `users/{uid}`
          // document without a name. That transaction deliberately never
          // overwrites an existing document — so nothing ever retried it, and
          // the Auth record kept a name the portal could not see.
          //
          // Backfill from the Auth record wherever the two disagree. It repairs
          // every account already hit, and because signup immediately navigates
          // to /verify-email, the next page load lands after `updateProfile`
          // has certainly completed — so this runs moments after the signup
          // that lost the race. The `!userProfile.displayName` guard means a
          // name the user later sets in Settings is never overwritten.
          if (userProfile) {
            const missing: Partial<UserProfile> = {};
            if (!userProfile.displayName && firebaseUser.displayName) {
              missing.displayName = firebaseUser.displayName;
            }
            if (!userProfile.photoURL && firebaseUser.photoURL) {
              missing.photoURL = firebaseUser.photoURL;
            }
            if (Object.keys(missing).length > 0) {
              try {
                await updateUserProfile(firebaseUser.uid, missing);
                userProfile = { ...userProfile, ...missing };
              } catch (err) {
                console.error("Backfilling profile from the Auth record failed:", err);
              }
            }
          }

          setProfile(userProfile);

          // ── Domain redirect enforcement ──────────────────────────────────
          // Only redirect on a fresh sign-in — never on a restored session
          // (page refresh, new tab, etc.). Unauthenticated visitors must
          // always stay on the domain they arrived on.
          if (isFreshSignIn && typeof window !== "undefined" && userProfile) {
            const storedDomain = userProfile.signup_domain ?? "www.scmsensei.ai";
            const currentHost  = window.location.hostname;

            // Redirect whenever the current host doesn't match the stored domain.
            // Covers both directions:
            //  - subdomain user visiting www → redirected to their subdomain
            //  - www user (or existing user with no signup_domain) visiting a subdomain → redirected to www
            if (currentHost !== storedDomain) {
              window.location.href =
                `https://${storedDomain}${window.location.pathname}${window.location.search}`;
            }
          }
        } else {
          setProfile(null);
        }

        setLoading(false);
      });

      return unsubscribe;
    });
  }, []);

  const handleSignInWithGoogle = async (): Promise<{ user: User; isNewUser: boolean }> => {
    _isFreshSignIn = true;
    return await signInWithGoogle();
  };

  const handleSignInWithEmail = async (email: string, password: string) => {
    _isFreshSignIn = true;
    await signInWithEmail(email, password);
  };

  const handleSignOut = async () => {
    await signOut();
    router.push("/login");
  };

  const handleRefreshProfile = async () => {
    if (!user) return;
    const refreshed = await getUserProfile(user.uid);
    setProfile(refreshed);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        loading,
        refreshProfile:   handleRefreshProfile,
        signInWithGoogle: handleSignInWithGoogle,
        signInWithEmail:  handleSignInWithEmail,
        signOut:          handleSignOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// ─── Hook (re-exported from useAuth.ts too) ───────────────────────────────────

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuthContext must be used inside <AuthProvider>");
  }
  return ctx;
}
